import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useTheme } from '../zustand/ThemeStore';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/config';
import { toast } from 'sonner';

interface CancellationFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CancellationFeedbackModal = ({ isOpen, onClose, onSuccess }: CancellationFeedbackModalProps) => {
  const darkMode = useTheme((state) => state.theme);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    helpfulnessRating: '',
    cancellationReason: '',
    experience: '',
    improvement: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'radio') {
      setFormData(prev => ({ ...prev, cancellationReason: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName || !formData.email || !formData.helpfulnessRating || !formData.cancellationReason) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getApiBaseUrl()}/api/v1/payment/cancel-subscription-with-feedback`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.status === 200) {
        toast.success('Thank you for your feedback. Your subscription has been cancelled.');
        onSuccess();
        onClose();
        
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          helpfulnessRating: '',
          cancellationReason: '',
          experience: '',
          improvement: ''
        });
        
        // Log out the user and redirect to login page
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Small delay to allow toast to show
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }
    } catch (error: any) {
      console.error('Error submitting cancellation feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 px-6 py-4 border-b ${
          darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                WE'RE SORRY TO SEE YOU GO
              </h2>
              <p className={`text-2xl sm:text-3xl font-bold mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Can you tell us why?
              </p>
              <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                We'd love to hear why you decided to leave. Your feedback helps us improve and support
                other business owners like you. This will only take a minute.
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className={`ml-4 p-2 rounded-full ${
                darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
              } transition-colors disabled:opacity-50`}
            >
              <X className={darkMode ? 'text-gray-400' : 'text-gray-600'} size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Full Name */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Full Name
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Full Name"
              disabled={loading}
              className={`w-full px-4 py-2 rounded-lg border ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-[#1a9f6b] disabled:opacity-50`}
            />
          </div>

          {/* Email */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              required
              disabled={loading}
              className={`w-full px-4 py-2 rounded-lg border ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-[#1a9f6b] disabled:opacity-50`}
            />
          </div>

          {/* Helpfulness Rating */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              1. On a scale of 1–10, how helpful did you find the Stride Tutorial Training Videos? <span className="text-red-500">*</span>
            </label>
            <select
              name="helpfulnessRating"
              value={formData.helpfulnessRating}
              onChange={handleChange}
              required
              disabled={loading}
              className={`w-full px-4 py-2 rounded-lg border ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-[#1a9f6b] disabled:opacity-50`}
            >
              <option value="">Select a rating</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>

          {/* Cancellation Reason */}
          <div>
            <label className={`block text-sm font-medium mb-3 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              3. What was the main reason you chose to cancel? <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {[
                'I found the platform too difficult to use',
                "I didn't have time to learn it",
                "I didn't see enough value for my business",
                'I had trouble migrating from my previous CRM',
                'It was missing features I needed',
                "I'm pausing my business or project"
              ].map((reason) => (
                <label key={reason} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="cancellationReason"
                    value={reason}
                    checked={formData.cancellationReason === reason}
                    onChange={handleChange}
                    disabled={loading}
                    className="mt-1 text-[#1a9f6b] focus:ring-[#1a9f6b] disabled:opacity-50"
                  />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {reason}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              4. What else can you tell us about your experience? We take this seriously so we can
              supply the best service possible. <span className="text-red-500">*</span>
            </label>
            <textarea
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              placeholder="Share your experience..."
              rows={4}
              required
              disabled={loading}
              className={`w-full px-4 py-2 rounded-lg border ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-[#1a9f6b] disabled:opacity-50 resize-none`}
            />
          </div>

          {/* Improvement */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              5. If you could wave a magic wand and change anything about the platform, what would
              it be? <span className="text-red-500">*</span>
            </label>
            <textarea
              name="improvement"
              value={formData.improvement}
              onChange={handleChange}
              placeholder="What would you change..."
              rows={4}
              required
              disabled={loading}
              className={`w-full px-4 py-2 rounded-lg border ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-[#1a9f6b] disabled:opacity-50 resize-none`}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={`px-6 py-2 rounded-lg font-medium ${
                darkMode
                  ? 'bg-gray-800 text-white hover:bg-gray-700'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              } transition-colors disabled:opacity-50`}
            >
              Keep Subscription
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  <span>Submitting...</span>
                </>
              ) : (
                <span>Submit & Cancel Subscription</span>
              )}
            </button>
          </div>
        </form>

        {/* Commented out embedded form
        <div className="relative" style={{ height: '70vh' }}>
          {!iframeLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-opacity-50">
              <div className="flex flex-col items-center space-y-4">
                <Loader2 className={`animate-spin ${darkMode ? 'text-white' : 'text-gray-900'}`} size={40} />
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Loading feedback form...
                </p>
              </div>
            </div>
          )}
          
          <div className="w-full h-full overflow-y-auto p-6">
            <iframe
              src="https://link.stridecrm.co/widget/form/pjrdtRZU2MNVIgFoqK3l"
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: '3px', minHeight: '1318px' }}
              id="inline-pjrdtRZU2MNVIgFoqK3l"
              data-layout="{'id':'INLINE'}"
              data-trigger-type="alwaysShow"
              data-trigger-value=""
              data-activation-type="alwaysActivated"
              data-activation-value=""
              data-deactivation-type="neverDeactivate"
              data-deactivation-value=""
              data-form-name="Cancellation Survey"
              data-height="1318"
              data-layout-iframe-id="inline-pjrdtRZU2MNVIgFoqK3l"
              data-form-id="pjrdtRZU2MNVIgFoqK3l"
              title="Cancellation Survey"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              onLoad={() => setIframeLoaded(true)}
            />
          </div>
        </div>

        <div className={`sticky bottom-0 z-10 px-6 py-4 border-t ${
          darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={`px-6 py-2 rounded-lg font-medium ${
                darkMode
                  ? 'bg-gray-800 text-white hover:bg-gray-700'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              } transition-colors disabled:opacity-50`}
            >
              Keep Subscription
            </button>
            {loading && (
              <div className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium flex items-center space-x-2">
                <Loader2 className="animate-spin" size={20} />
                <span>Cancelling Subscription...</span>
              </div>
            )}
          </div>
        </div>
        */}
      </div>
    </div>
  );
};
